import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Hash, Play } from "lucide-react";

// A small sample menu the operator can push and then walk through in the simulator.
const SAMPLE_MENU = {
  root: { text: "Welcome to Kadana Demo\n1. Balance\n2. Pay\n0. Cancel", options: { "1": "balance", "2": "pay" } },
  balance: { text: "Your balance is GBP 12.50.\n0. Cancel" },
  pay: { text: "Payment started. You'll get an SMS.\n0. Cancel" },
};

export default function USSD() {
  const { org } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [gateways, setGateways] = useState([]);
  const [sessionId] = useState(() => `sim-${Math.random().toString(36).slice(2, 10)}`);
  const [screen, setScreen] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (org?.id) api.get(`/orgs/${org.id}/projects`).then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.projects || []);
      setProjects(list); if (list[0]) setPid(list[0].id);
    }).catch((e) => toast.error(errMsg(e)));
    api.get("/ussd/gateways").then((r) => setGateways(r.data.gateways || [])).catch(() => {});
  }, [org]);

  const step = async (input) => {
    if (!pid) return;
    setBusy(true);
    try {
      if (input === null) await api.post("/ussd/menu", { project_id: pid, menu: SAMPLE_MENU });
      const { data } = await api.post("/ussd/simulate", { project_id: pid, session_id: sessionId, input });
      setScreen(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Shell>
      <h1 className="k-heading" style={{ fontSize: 30, margin: 0 }}>USSD</h1>

      <div className="k-container" style={{ padding: 20, marginTop: 16 }} data-testid="ussd-gateways">
        <h3 className="k-heading" style={{ fontSize: 16, marginTop: 0 }}>Verified gateways</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {gateways.map((g, i) => <span key={i} className="k-badge k-badge-sandbox">{g.name || g.id || String(g)}</span>)}
          {gateways.length === 0 && <span className="k-muted" style={{ fontSize: 12 }}>Loading…</span>}
        </div>
      </div>

      <div className="k-container" style={{ padding: 20, marginTop: 16 }} data-testid="ussd-simulator">
        <label className="k-muted" style={{ fontSize: 12 }}>Project</label>
        <select className="k-control" data-testid="ussd-project-select" value={pid} onChange={(e) => setPid(e.target.value)} style={{ marginTop: 4, marginBottom: 12 }}>
          {projects.length === 0 && <option value="">No projects yet</option>}
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>

        <button className="k-btn k-btn-primary" data-testid="ussd-dial" data-loading={busy} disabled={!pid || busy} onClick={() => step(null)}>
          <Play size={14} /> Dial *384#
        </button>

        {screen && (
          <div style={{ marginTop: 14 }}>
            <pre className="k-control" data-testid="ussd-screen" style={{ whiteSpace: "pre-wrap", fontSize: 13, minHeight: 90 }}>{screen.display}</pre>
            {screen.continue && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                {["1", "2", "3", "0"].map((d) => (
                  <button key={d} className="k-btn k-btn-ghost" data-testid={`ussd-key-${d}`} disabled={busy} onClick={() => step(d)} style={{ minWidth: 44 }}>{d}</button>
                ))}
              </div>
            )}
            {screen.continue === false && <p className="k-muted" data-testid="ussd-ended" style={{ fontSize: 12, marginTop: 8 }}>Session ended.</p>}
          </div>
        )}
      </div>
    </Shell>
  );
}
